import { useState } from 'react';
import { useNavigate } from 'react-router';
import { modals } from '@mantine/modals';
import { Title, Button, Flex, Loader, Alert, Box, Text, Center } from '@mantine/core';
import { trpc } from '../lib/trpc';
import DataGrid from '../components/DataGrid';
import SoftDeletePanel from '../components/SoftDeletePanel';
import SortableGrid from '../components/SortableGrid';
import CsvExport from '../components/CsvExport';
import type { SectionConfig } from '../lib/types';

interface SectionListProps {
  config: SectionConfig;
}

interface SortableItem {
  id: number;
  [key: string]: unknown;
}

export default function SectionList({ config }: SectionListProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>(config.defaultSort?.column);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(config.defaultSort?.direction ?? 'asc');
  const [reorderMode, setReorderMode] = useState(false);

  const utils = trpc.useUtils();

  const listQuery = trpc.admin.crud.list.useQuery({
    entity: config.entity,
    page,
    pageSize: 25,
    sortBy,
    sortDir,
    search: search || undefined,
  });

  const deletedQuery = trpc.admin.crud.list.useQuery(
    { entity: config.entity, page: 1, pageSize: 50, recentlyDeleted: true },
    { enabled: config.softDelete === true },
  );

  const deleteMutation = trpc.admin.crud.delete.useMutation({
    onSuccess: () => {
      void utils.admin.crud.list.invalidate({ entity: config.entity });
    },
  });

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handleSortChange(column: string, dir: 'asc' | 'desc') {
    setSortBy(column);
    setSortDir(dir);
    setPage(1);
  }

  function handleRowClick(row: Record<string, unknown>) {
    const id = row['id'];
    navigate(String(id));
  }

  function handleDelete(row: Record<string, unknown>) {
    const numId = Number(row['id']);
    if (Number.isNaN(numId)) return;
    modals.openConfirmModal({
      title: 'Delete record',
      children: <Text size="sm">Delete this record? This can be undone if the section supports it.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMutation.mutate({ entity: config.entity, id: numId }),
    });
  }

  function handleReorderComplete() {
    void utils.admin.crud.list.invalidate({ entity: config.entity });
  }

  function handleRestored() {
    void utils.admin.crud.list.invalidate({ entity: config.entity });
    void deletedQuery.refetch();
  }

  const data = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const deletedItems = deletedQuery.data?.rows ?? [];

  const draggableLabelField = config.formFields.find(
    (f) => ['name', 'title', 'label'].includes(f.key),
  )?.key ?? 'id';

  const sortableItems: SortableItem[] = data.map((row) => ({
    ...row,
    id: Number(row['id']),
  }));

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="md">
        <Title order={2}>{config.title}</Title>
        <Flex gap="sm" align="center">
          <CsvExport entity={config.entity} columns={config.columns} />
          {config.draggable && (
            <Button variant="default" onClick={() => setReorderMode((m) => !m)}>
              {reorderMode ? 'Done Reordering' : 'Reorder'}
            </Button>
          )}
          {!config.readOnly && (
            <Button onClick={() => navigate('new')}>Add New Item</Button>
          )}
        </Flex>
      </Flex>

      {listQuery.isLoading && <Center h="60vh"><Loader /></Center>}
      {listQuery.isError && <Alert color="red">Error loading data.</Alert>}

      {!listQuery.isLoading && (
        <>
          {reorderMode && config.draggable ? (
            <SortableGrid
              entity={config.entity}
              items={sortableItems}
              labelField={draggableLabelField}
              onReorderComplete={handleReorderComplete}
            />
          ) : (
            <DataGrid
              data={data}
              columns={config.columns}
              total={total}
              page={page}
              pageSize={25}
              onPageChange={setPage}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              search={search}
              onSearchChange={handleSearchChange}
              onRowClick={config.readOnly ? undefined : handleRowClick}
              onDelete={config.readOnly ? undefined : handleDelete}
            />
          )}

          {config.softDelete && deletedItems.length > 0 && (
            <SoftDeletePanel
              config={config}
              deletedItems={deletedItems}
              onRestored={handleRestored}
            />
          )}
        </>
      )}
    </Box>
  );
}
